import React, { useState, useEffect } from 'react';
import { Heart, ExternalLink, Edit, Trash2, Ban, Zap } from 'lucide-react';
import { doc, getDoc, setDoc, deleteDoc, updateDoc, increment } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Deal, getHighCommissionLink } from '../utils/constants';

interface DealCardProps {
  deal: Deal;
  isAdmin: boolean;
  isSelected?: boolean;
  priority?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit?: (deal: Deal) => void;
  onDelete?: (id: string) => void;
  onBlock?: (id: string) => void;
}

export default function DealCard({ deal, isAdmin, isSelected, priority = false, onToggleSelect, onEdit, onDelete, onBlock }: DealCardProps) {
  const [isSaved, setIsSaved] = useState(false);
  const [clickCount, setClickCount] = useState(deal.click_count || 0);

  useEffect(() => {
    const checkSaved = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && deal.id) {
        const savedRef = doc(db, `users/${currentUser.uid}/savedDeals`, deal.id);
        const docSnap = await getDoc(savedRef);
        setIsSaved(docSnap.exists());
      }
    };
    checkSaved();
  }, [deal.id]);

  const handleSave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const currentUser = auth.currentUser;
    if (!currentUser) {
        return; 
    }

    if (!deal.id) {
        return;
    }

    const savedRef = doc(db, `users/${currentUser.uid}/savedDeals`, deal.id);
    
    try {
      if (isSaved) {
        await deleteDoc(savedRef);
        setIsSaved(false);
      } else {
        await setDoc(savedRef, {
          dealId: deal.id,
          savedAt: Date.now(),
          title: deal.title,
          image: deal.image,
          price: deal.price,
          url: deal.url
        });
        setIsSaved(true);
      }
    } catch (error: any) {
      console.error("Error saving:", error);
    }
  };

  const handleOutboundClick = async () => {
    setClickCount(prev => prev + 1);
    if (deal.id) {
      try {
        await updateDoc(doc(db, 'deals', deal.id), {
          click_count: increment(1)
        });
      } catch (err) {
        console.error("Failed to count click", err);
      }
    }
  };

  const discount = deal.originalPrice > deal.price 
    ? Math.round(((deal.originalPrice - deal.price) / deal.originalPrice) * 100) 
    : 0;

  const finalUrl = getHighCommissionLink ? getHighCommissionLink(deal.url) : deal.url;

  return (
    <div className={`relative bg-white rounded-2xl shadow-sm border ${isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'} overflow-hidden flex flex-col transition-all hover:shadow-xl group`}>
      
      {isAdmin && onToggleSelect && (
        <div className="absolute top-3 left-3 z-20 bg-white/90 backdrop-blur-sm p-1 rounded-md shadow-sm">
          <input 
            type="checkbox" 
            checked={isSelected} 
            onChange={() => onToggleSelect(deal.id!)} 
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" 
          />
        </div>
      )}

      {/* 📸 UPGRADED IMAGE SECTION: Edge-to-edge, larger height, cover fit */}
      <a href={finalUrl} target="_blank" rel="noopener noreferrer" onClick={handleOutboundClick} className="relative block h-60 bg-gray-100 overflow-hidden border-b border-slate-100">
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-10 items-end">
          {discount > 0 && (
            <span className="bg-red-500 text-white text-xs font-black px-2.5 py-1 rounded-md shadow-sm">
              -{discount}%
            </span>
          )}
          {/* 🆕 Changed "Glitch" to "Essential" and swapped yellow for premium slate/blue */}
          {deal.dealType === 'Glitch' && (
            <span className="bg-slate-900 text-white text-[10px] uppercase tracking-widest font-black px-2.5 py-1.5 rounded-md flex items-center shadow-sm">
              <Zap className="w-3 h-3 mr-1 text-blue-400" /> ESSENTIAL
            </span>
          )}
          {deal.promoType && deal.promoType !== 'None' && (
            <span className="bg-blue-600 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-sm">
              {deal.promoType}
            </span>
          )}
        </div>
        <img 
          src={deal.image || 'https://placehold.co/600x400/gray/white?text=No+Image'} 
          alt={deal.title} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          loading={priority ? "eager" : "lazy"} 
        />
      </a>

      <div className="p-5 flex flex-col flex-1">
         <div className="flex justify-between items-start mb-2 gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
                {deal.vehicle && (
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider">
                    {deal.vehicle}
                  </span>
                )}
                {deal.brand && (
                  <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md uppercase tracking-wider border border-blue-100">
                    {deal.brand}
                  </span>
                )}
            </div>
            <button onClick={handleSave} className="text-slate-300 hover:text-red-500 transition-colors p-1 z-10 -mr-1 -mt-1">
              <Heart className={`w-5 h-5 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
            </button>
         </div>

         <a href={finalUrl} target="_blank" rel="noopener noreferrer" onClick={handleOutboundClick}>
           <h3 className="font-bold text-slate-800 leading-snug mb-3 line-clamp-2 hover:text-blue-600 transition-colors">
             {deal.title}
           </h3>
         </a>

         {/* 🆕 Upgraded Curator Note: Slate background instead of yellow */}
         {deal.curatorNote && (
           <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 flex items-start gap-2">
              <Zap className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span className="font-medium leading-relaxed">{deal.curatorNote}</span>
           </div>
         )}

         <div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{deal.store || 'Retailer'}</span>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-black text-slate-900">${deal.price.toFixed(2)}</span>
                {deal.originalPrice > deal.price && (
                  <span className="text-sm text-slate-400 line-through font-medium">${deal.originalPrice.toFixed(2)}</span>
                )}
              </div>
            </div>
            <a href={finalUrl} target="_blank" rel="noopener noreferrer" onClick={handleOutboundClick} className="bg-slate-900 hover:bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-md transition-colors flex items-center gap-2">
              Shop <ExternalLink className="w-4 h-4" />
            </a>
         </div>
      </div>

      {isAdmin && (
        <div className="bg-slate-900 p-3 flex justify-between items-center text-xs text-slate-400 border-t border-slate-800">
           <div className="flex gap-4 font-mono">
             <span title="Outbound Clicks">🖱️ {clickCount}</span>
             <span className={deal.inStock === false ? 'text-red-400 font-bold' : 'text-green-400'}>
               {deal.inStock === false ? 'HIDDEN' : 'LIVE'}
             </span>
           </div>
           <div className="flex gap-3">
             {onEdit && <button onClick={() => onEdit(deal)} className="hover:text-white transition-colors"><Edit className="w-4 h-4" /></button>}
             {onDelete && <button onClick={() => onDelete(deal.id!)} className="hover:text-orange-400 transition-colors"><Trash2 className="w-4 h-4" /></button>}
             {onBlock && <button onClick={() => onBlock(deal.id!)} className="hover:text-red-500 transition-colors"><Ban className="w-4 h-4" /></button>}
           </div>
        </div>
      )}
    </div>
  );
}